// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Where the hole view gets its aerial imagery.
 *
 * Esri's World Imagery basemap is the default: it needs no key and covers golf courses
 * at the zoom levels this view uses. Its terms require the attribution rendered on the
 * view, so keep that visible if you swap providers — and set the attribution to match.
 *
 * Both are overridable so a deployment can point at its own tile cache or a licensed
 * provider without a code change. Read `process.env` with a plain member access:
 * babel-preset-expo only inlines that form, and `process.env?.X` silently becomes
 * undefined on native.
 */

const DEFAULT_TILE_URL_TEMPLATE =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

const DEFAULT_ATTRIBUTION = 'Imagery © Esri, Maxar, Earthstar Geographics';

export const getSatelliteTileUrlTemplate = (): string => {
  const configured = process.env.EXPO_PUBLIC_SATELLITE_TILE_URL;
  return typeof configured === 'string' && configured.trim().length > 0
    ? configured.trim()
    : DEFAULT_TILE_URL_TEMPLATE;
};

export const getSatelliteAttribution = (): string => {
  const configured = process.env.EXPO_PUBLIC_SATELLITE_ATTRIBUTION;
  return typeof configured === 'string' && configured.trim().length > 0
    ? configured.trim()
    : DEFAULT_ATTRIBUTION;
};

/**
 * Credit for the stored whole-course basemap (see HoleMapCanvas/HoleViewModal's
 * `storedBasemap`/`lantmaterietImage`), never the live tile source above. Lantmateriet's
 * Ortofoto is CC BY 4.0, which is what makes storing and redistributing the picture allowed
 * in the first place (see course-analysis-service/README.md's licensing section) — unlike
 * Esri, whose terms permit tracing but not retaining the imagery. That licence requires
 * this credit wherever the image is shown, so it is not overridable like the tile source is.
 */
export const LANTMATERIET_ATTRIBUTION = 'Imagery © Lantmäteriet';
